interface SectionSkeletonProps {
  type?: "spinner" | "pills" | "cards" | "list" | "text";
  count?: number;
  columns?: number;
  minHeight?: string;
  className?: string;
  surface?: "white" | "alt";
  aspectRatio?: "16/10" | "4/3";
  footerLines?: number;
  showAvatar?: boolean;
}

const TEXT_WIDTHS = ["w-full", "w-5/6", "w-4/5", "w-3/4", "w-2/3"];

const COL_MAP: Record<number, string> = {
  1: "lg:grid-cols-1",
  2: "lg:grid-cols-2",
  3: "lg:grid-cols-3",
  4: "lg:grid-cols-4",
  5: "lg:grid-cols-5",
  6: "lg:grid-cols-6",
};

/**
 * Reusable skeleton / loading placeholder component.
 * Replaces all inline `animate-pulse` and spinner patterns across pages.
 */
export default function SectionSkeleton({
  type = "spinner",
  count = 3,
  columns = 3,
  minHeight = "50vh",
  className = "",
  surface = "white",
  aspectRatio = "16/10",
  footerLines = 2,
  showAvatar = false,
}: SectionSkeletonProps) {
  if (type === "spinner") {
    return (
      <div style={{ minHeight }} className={`flex items-center justify-center ${className}`}>
        <div className="animate-spin w-8 h-8 border-2 border-primary border-t-transparent rounded-full" />
      </div>
    );
  }

  if (type === "pills") {
    return (
      <div className={`flex gap-2 flex-wrap ${className}`}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="h-8 w-16 bg-slate-100 rounded-full animate-pulse" />
        ))}
      </div>
    );
  }

  if (type === "cards") {
    const surfaceClass = surface === "alt" ? "bg-surface-alt" : "bg-white";
    const aspectClass = aspectRatio === "4/3" ? "aspect-[4/3]" : "aspect-[16/10]";
    const colClass = COL_MAP[columns] || "lg:grid-cols-3";
    return (
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${colClass} gap-4 md:gap-6 ${className}`}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className={`${surfaceClass} rounded-xl overflow-hidden card-shadow animate-pulse`}>
            <div className={`${aspectClass} bg-slate-200`} />
            <div className="p-4 md:p-5 space-y-2">
              {Array.from({ length: footerLines }).map((_, j) => (
                <div
                  key={j}
                  className={`bg-slate-200 rounded ${j === 0 ? "h-4 w-full" : j === 1 ? "h-3 w-3/4" : "h-3 w-1/2"}`}
                />
              ))}
              {showAvatar && (
                <div className="flex items-center gap-2 pt-1">
                  <div className="w-6 h-6 rounded-full bg-slate-200" />
                  <div className="h-3 bg-slate-200 rounded w-16" />
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (type === "list") {
    return (
      <div className={`space-y-4 ${className}`}>
        {Array.from({ length: count }).map((_, i) => (
          <div key={i} className="flex gap-4 p-4 bg-white rounded-xl border border-slate-100 animate-pulse">
            <div className="w-24 h-24 md:w-32 md:h-32 shrink-0 rounded-lg bg-slate-200" />
            <div className="flex-1 space-y-3 py-2">
              <div className="h-4 bg-slate-200 rounded w-24" />
              <div className="h-5 bg-slate-200 rounded w-3/4" />
              <div className="h-3 bg-slate-200 rounded w-full" />
              <div className="h-3 bg-slate-200 rounded w-1/2" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  // text
  return (
    <div className={`space-y-3 ${className}`}>
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className={`h-4 bg-slate-200 rounded animate-pulse ${TEXT_WIDTHS[i % TEXT_WIDTHS.length]}`}
        />
      ))}
    </div>
  );
}